import {
  Button,
  ColorPicker,
  Descriptions,
  Flex,
  Image,
  Modal,
  notification,
  Switch,
  Upload,
} from "antd";
import React, { useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { updateCmsConfigurationsBanner } from "../../Services/cms_configuration";

function BannerPage({
  bannerBgColor,
  bannerBgImage,
  baseURL,
  onMutipleUpdate,
}) {
  const [logoOption, setLogoOption] = useState("default");
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customColor, setCustomColor] = useState(bannerBgColor?.custom_value);

  const handleColorChange = (color) => {
    // Get hex string from color object
    const hex = color.toHexString();
    setCustomColor(hex);
  };

  const handlePreview = async (file) => {
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleLogoChange = (e) => {
    setLogoOption(e);
  };

  const handleChange = ({ fileList }) => {
    // Manually set the preview image for the newly uploaded file
    setFileList(
      fileList.map((file) => ({
        ...file,
        preview:
          file.url ||
          (file.originFileObj && URL.createObjectURL(file.originFileObj)),
      }))
    );
  };

  useEffect(() => {
    if (bannerBgImage?.custom_value) {
      setFileList([
        {
          uid: "-1",
          name: "custom_logo.png",
          status: "done",
          url: `${baseURL}/${bannerBgImage?.custom_value}`,
        },
      ]);
      setLogoOption(bannerBgColor?.use_custom ? "custom" : "default");
    }
    setCustomColor(bannerBgColor?.custom_value);
  }, [bannerBgImage, baseURL, bannerBgColor]);

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const editMutation = useMutation({
    mutationFn: updateCmsConfigurationsBanner,
  });

  const updateBannerImage = (values) => {
    editMutation.mutate({ ...values });
  };

  const handleSave = () => {
    if (fileList.length > 0 && fileList[0]?.originFileObj) {
      let payload = {
        id: bannerBgImage?.id,
      };
      payload.image = fileList[0].originFileObj;
      updateBannerImage(payload);
    } else if (
      bannerBgImage?.custom_value &&
      fileList.length === 0 &&
      logoOption !== "custom"
    ) {
      let payload = {
        id: bannerBgImage?.id,
        is_delete_image: true,
      };
      updateBannerImage(payload);
    } else if (logoOption === "custom" && fileList.length === 0) {
      return notification.error({
        message: "Error",
        description: "Please Upload Custom Banner Image",
        duration: 3,
      });
    }
    let bannerImagePayload = {
      id: bannerBgImage?.id,
      use_custom: logoOption === "custom" && fileList.length > 0 ? true : false,
    };

    let bannerColorPayload = {
      id: bannerBgColor?.id,
      use_custom: logoOption === "custom" ? true : false,
      custom_value: customColor,
    };

    onMutipleUpdate({
      configs: [bannerImagePayload, bannerColorPayload],
    });
  };

  return (
    <div>
      <Descriptions column={2} bordered>
        <Descriptions.Item label="Default Banner">
          <Image
            width={100}
            src={`${baseURL}${bannerBgImage?.default_value}`}
            alt="Default Logo"
            preview={{
              toolbarRender: () => null,
            }}
          />
        </Descriptions.Item>

        <Descriptions.Item label="Custom Banner">
          <Upload
            //   action="https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload" // <-- Replace with your API
            listType="picture-card"
            fileList={fileList}
            onPreview={handlePreview}
            onChange={handleChange}
            beforeUpload={() => false}
            maxCount={1}
            accept=".jpg,.jpeg,.png,.svg"
            onRemove={async (file) => {
              return new Promise((resolve, reject) => {
                Modal.confirm({
                  title: "Are you sure you want to delete this image?",
                  okText: "Yes",
                  cancelText: "No",
                  onOk: () => {
                    resolve(true); // allow deletion
                  },
                  onCancel: () => {
                    resolve(false); // cancel deletion
                  },
                });
              });
            }}
          >
            {fileList.length >= 1 ? null : uploadButton}
          </Upload>
          {previewImage && (
            <Image
              wrapperStyle={{ display: "none" }}
              preview={{
                visible: previewOpen,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(""),
                toolbarRender: () => null,
              }}
              src={previewImage}
            />
          )}
          <small>Recommended sizes: 500×500 px, 750×750 px</small>
          <br />
          <small>Aspect ratio: 1:1</small>
          <br />
          <small>File size: 100–500 KB</small>
        </Descriptions.Item>
        {bannerBgColor && bannerBgColor.default_value && (
          <>
            <Descriptions.Item label="Default Background Color">
              <ColorPicker
                defaultValue={bannerBgColor?.default_value}
                size="large"
                showText
                disabled
              />
            </Descriptions.Item>

            <Descriptions.Item label="Custom Background Color">
              <ColorPicker
                defaultValue={bannerBgColor?.custom_value}
                size="large"
                showText
                onChange={handleColorChange}
                disabledAlpha
              />
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Use Custom">
          <Switch
            checked={logoOption === "custom"}
            onChange={(checked) =>
              handleLogoChange(checked ? "custom" : "default")
            }
            checkedChildren="Custom"
            unCheckedChildren="Default"
          />
        </Descriptions.Item>
      </Descriptions>

      <Flex className="mt-5" justify="flex-end">
        <Button type="primary" onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </div>
  );
}

export default BannerPage;
